import {type BlobOffset, formatBlobRangeAnchor, parseBlobRange, parseFileAnchor} from '@github-ui/blob-anchor'
import type {CodeSection, CodeSectionLookup} from '@github-ui/code-nav'
import {CodeownersErrorLineIndicator} from '@github-ui/code-view-shared/components/CodeownersValidationBanner'
import {useReposAnalytics} from '@github-ui/code-view-shared/hooks/use-repos-analytics'
import {updateUrlHash} from '@github-ui/history'
import {useCodeViewOptions} from '@github-ui/use-code-view-options'
import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {useLayoutEffect} from 'react'
import React, {type PropsWithChildren, useCallback, useEffect, useRef, useState} from 'react'

import {useCursorSelectionRef} from '../../../../hooks/use-cursor-navigation'
import type {SetStickyLinesType} from '../../../../hooks/use-sticky-lines'
import {
  calculateLineAndColumn,
  collapseRow,
  expandAllRows,
  expandRow,
  useIsLineCollapsed,
} from '../../../../utilities/lines'
import {HiddenUnicodeTooltip} from '../../Banners/HiddenUnicode'
import {CopilotButton, type CopilotButtonHandle} from '../CopilotButton'
import type {HighlightedLineMenuHandle} from '../HighlightedLineMenu'
import HighlightedLineMenu from '../HighlightedLineMenu'
import {useHighlightedLinesInfo} from './HighlightedLineProvider'
import type {CodeLineData} from './hooks/use-code-lines'
import lineNumberStyles from './LineNumber.module.css'

export interface LineNumberProps {
  id?: string
  onClick?: React.MouseEventHandler<HTMLDivElement>
  codeLineData: CodeLineData
  ownedCodeSections?: Map<number, CodeSection[]> | null
  onCollapseToggle?: (isCollapsed: boolean) => void
  preventClick?: boolean
  onLineStickOrUnstick?: SetStickyLinesType
  virtualOffset?: number
  codeLineToSectionMap?: CodeSectionLookup
  copilotAccessAllowed?: boolean
}

export const LineNumber = React.memo(LineNumberUnmemoized)

function LineNumberUnmemoized({
  codeLineData,
  onClick,
  ownedCodeSections,
  onCollapseToggle,
  preventClick,
  onLineStickOrUnstick,
  virtualOffset,
  copilotAccessAllowed,
}: PropsWithChildren<LineNumberProps>) {
  const {lineNumber, ownedSection, codeLineClassName, isStartLine, codeownersLineError, hiddenUnicode} = codeLineData
  const {sendRepoClickEvent} = useReposAnalytics()
  const selectionRef = useCursorSelectionRef()
  const [shouldShowCopilotButton, setShouldShowCopilotButton] = useState(false)

  const codeCellClickFunc: React.MouseEventHandler<HTMLDivElement> = useCallback(
    event => {
      if (event.defaultPrevented) {
        return
      }
      const targetedLineNumber = parseInt(event.currentTarget.getAttribute('data-line-number') || '', 10)

      let anchorInfo = parseFileAnchor(`L${targetedLineNumber}`)

      let highlightStart: BlobOffset | undefined = undefined
      let highlightEnd: BlobOffset | undefined = undefined

      const cursorSelection = selectionRef.current
      if (
        cursorSelection &&
        !(
          cursorSelection.start.line === cursorSelection.end.line &&
          cursorSelection.start.column === cursorSelection.end.column
        )
      ) {
        highlightStart = cursorSelection.start
        highlightEnd = cursorSelection.end
      } else {
        const selectionRange = window.getSelection()?.rangeCount ? window.getSelection()?.getRangeAt(0) : null
        if (selectionRange) {
          highlightStart = calculateLineAndColumn(selectionRange.startContainer, selectionRange.startOffset)
          highlightEnd = calculateLineAndColumn(selectionRange.endContainer, selectionRange.endOffset)
        }
      }

      let selection = false
      if (
        highlightStart &&
        highlightEnd &&
        highlightStart.line <= targetedLineNumber &&
        highlightEnd.line >= targetedLineNumber
      ) {
        selection = true

        anchorInfo = {
          anchorPrefix: '',
          blobRange: {
            start: highlightStart,
            end: highlightEnd,
          },
        }
      }

      const {blobRange} = anchorInfo

      const currentLines = parseBlobRange(window.location.hash)
      if (currentLines && event.shiftKey && !selection) {
        sendRepoClickEvent('BLOB.MULTILINE')
        anchorInfo.blobRange = {
          start: currentLines.start,
          end: blobRange.end,
        }
      } else {
        sendRepoClickEvent('BLOB.LINE')
      }

      const hash = formatBlobRangeAnchor(anchorInfo)
      updateUrlHash(hash)

      onClick?.(event)
    },
    [onClick, selectionRef, sendRepoClickEvent],
  )

  const highlightedInfo = useHighlightedLinesInfo(lineNumber)
  const isFirstHighlightedLine = highlightedInfo?.start.line === lineNumber

  // calculates if the line is fully within the highlight info
  // all lines numbers except the first line are considered fully highlight
  // the first line is considered fully highlighted if there is no column offset
  const isInHighlightRange =
    highlightedInfo && highlightedInfo.start.line < lineNumber && highlightedInfo.end.line >= lineNumber
  const isFullyHighlighted = isInHighlightRange || (isFirstHighlightedLine && highlightedInfo?.start.column === null)

  const menuAnchorRef = useRef<HTMLDivElement | null>(null)
  const menuRef = useRef<HighlightedLineMenuHandle>(null)
  const copilotMenuRef = useRef<CopilotButtonHandle>(null)

  useLayoutEffect(() => {
    if (isFirstHighlightedLine) {
      menuRef.current?.setAnchor(menuAnchorRef.current)
    }
  }, [isFirstHighlightedLine])

  const {codeFoldingOption} = useCodeViewOptions()
  const showCodeFoldingIcon = codeFoldingOption.enabled

  useEffect(() => {
    if (!codeFoldingOption.enabled) {
      expandAllRows()
      onCollapseToggle?.(false)
    }
  }, [codeFoldingOption.enabled, onCollapseToggle])

  const mouseCallback = (isEnter: boolean) => {
    if (copilotAccessAllowed) {
      setShouldShowCopilotButton(isEnter)
      //allow the state change to flush before setting the anchor with a 0ms timeout
      setTimeout(() => copilotMenuRef.current?.setAnchor(isEnter ? menuAnchorRef.current : null), 0)
    }
  }

  return (
    <>
      <div
        ref={menuAnchorRef}
        data-line-number={lineNumber}
        className={clsx(
          codeLineClassName,
          'react-line-number react-code-text',
          virtualOffset && 'virtual',
          preventClick && 'prevent-click',
          isFullyHighlighted && 'highlighted-line',
        )}
        style={virtualOffset ? {paddingRight: '16px', top: `${virtualOffset}px`} : {paddingRight: '16px'}}
        onMouseDown={preventClick ? undefined : codeCellClickFunc}
        onMouseEnter={copilotAccessAllowed ? () => mouseCallback(true) : undefined}
        onMouseLeave={copilotAccessAllowed ? () => mouseCallback(false) : undefined}
      >
        {lineNumber}
        {codeownersLineError && (
          <CodeAlert>
            <CodeownersErrorLineIndicator />
          </CodeAlert>
        )}
        {hiddenUnicode && (
          <CodeAlert>
            <HiddenUnicodeTooltip />
          </CodeAlert>
        )}
        {showCodeFoldingIcon && isStartLine && ownedSection && ownedCodeSections && (
          <CodeAlert displayRight>
            <CodeFoldingChevron
              codeLineData={codeLineData}
              onCollapseToggle={onCollapseToggle}
              onLineStickOrUnstick={onLineStickOrUnstick}
            />
          </CodeAlert>
        )}
      </div>
      {isFirstHighlightedLine && (
        <HighlightedLineMenu
          codeLineClassName={codeLineClassName}
          ref={menuRef}
          rowBeginId={`LG${highlightedInfo.start.line}`}
          rowBeginNumber={highlightedInfo.start.line}
          rowEndNumber={highlightedInfo.end.line}
          rowEndId={`LG${highlightedInfo.end.line}`}
        />
      )}
      {copilotAccessAllowed && shouldShowCopilotButton && (
        <div>
          <CopilotButton
            ref={copilotMenuRef}
            rowBeginNumber={lineNumber}
            rowEndNumber={lineNumber}
            id="code-line-copilot-button"
          />
        </div>
      )}
    </>
  )
}

interface CodeAlertProps {
  displayRight?: boolean
}

export function CodeAlert({children, displayRight}: PropsWithChildren<CodeAlertProps>) {
  return (
    <span className={clsx(lineNumberStyles.codeAlert, displayRight && lineNumberStyles.codeAlertRight)}>
      {children}
    </span>
  )
}

export function CodeFoldingChevron({
  codeLineData,
  onCollapseToggle,
  onLineStickOrUnstick,
}: {
  codeLineData: CodeLineData
  onCollapseToggle?: (isCollapsed: boolean) => void
  onLineStickOrUnstick?: SetStickyLinesType
}) {
  const collapsed = useIsLineCollapsed(codeLineData.lineNumber)

  const expand = useCallback(
    (event: React.MouseEvent) => {
      const {lineNumber} = codeLineData
      onCollapseToggle?.(false)
      expandRow(lineNumber)
      onLineStickOrUnstick?.(codeLineData, true)
      event.preventDefault()
    },
    [codeLineData, onCollapseToggle, onLineStickOrUnstick],
  )

  const collapse = useCallback(
    (event: React.MouseEvent) => {
      const {lineNumber} = codeLineData
      onCollapseToggle?.(true)
      collapseRow(lineNumber)
      event.preventDefault()
    },
    [codeLineData, onCollapseToggle],
  )

  return collapsed ? (
    <div
      aria-label={'Expand code section'}
      onMouseDown={expand}
      role="button"
      tabIndex={0}
      className={lineNumberStyles.codeFoldingChevron}
    >
      <ChevronRightIcon />
    </div>
  ) : (
    <div
      aria-label={'Collapse code section'}
      onMouseDown={collapse}
      role="button"
      tabIndex={0}
      className={lineNumberStyles.codeFoldingChevron}
    >
      <ChevronDownIcon />
    </div>
  )
}
